import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Tabs,
  Tab,
  Grid,
  Card,
  CardContent,
  Switch,
  FormControlLabel,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  Divider,
  Avatar,
  Slider,
  Alert,
  Snackbar,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Stack,
} from '@mui/material';
import {
  Settings as SettingsIcon,
  Person as PersonIcon,
  Security as SecurityIcon,
  Notifications as NotificationsIcon,
  Palette as PaletteIcon,
  Language as LanguageIcon,
  Storage as StorageIcon,
  Api as ApiIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Save as SaveIcon,
  RestoreFromTrash as RestoreIcon,
} from '@mui/icons-material';
import FullscreenButton from '../components/FullscreenButton';
import UsageDashboard from '../components/LLMUsage/UsageDashboard';
import TenantHomeDestinationPanel from '../components/TenantHomeDestinationPanel';

function Settings() {
  const [selectedTab, setSelectedTab] = useState(0);
  const [tenantHome, setTenantHome] = useState(null);
  const [tenantId, setTenantId] = useState(null);
  const [canEditTenantHome, setCanEditTenantHome] = useState(false);
  const [settings, setSettings] = useState({
    // Profile settings
    username: '',
    email: '',
    firstName: '',
    lastName: '',
    avatar: '',
    timezone: 'UTC',
    language: 'en',

    // Appearance settings
    theme: 'light',
    fontSize: 'medium',
    compactMode: false,
    animationsEnabled: true,

    // Privacy & Security
    twoFactorEnabled: false,
    sessionTimeout: 30,
    loginNotifications: true,
    dataRetention: 90,

    // Notifications
    emailNotifications: true,
    pushNotifications: false,
    formSubmissionAlerts: true,
    weeklyReports: true,

    // Chat & AI settings
    defaultModel: 'llama4',
    temperature: 0.7,
    maxTokens: 3000,
    autoPlayAudio: false,
    speechRecognition: true,

    // API settings
    apiKeys: [],
    webhooks: [],
    integrations: [],
  });

  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const [apiKeyDialog, setApiKeyDialog] = useState(false);
  const [webhookDialog, setWebhookDialog] = useState(false);
  const [newApiKey, setNewApiKey] = useState({ name: '', key: '', description: '' });
  const [newWebhook, setNewWebhook] = useState({ name: '', url: '', events: [] });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      // Load user settings from backend
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const savedSettings = localStorage.getItem('userSettings');

      if (savedSettings) {
        setSettings({ ...settings, ...JSON.parse(savedSettings) });
      }

      // Set user info from stored data
      setSettings((prev) => ({
        ...prev,
        username: user.username || localStorage.getItem('username') || '',
        email: user.email || '',
        theme: 'light',
      }));

      const role = user.role || '';
      const tid = user.tenant?._id || user.tenant || localStorage.getItem('tenant_id');
      const adminOk = ['admin', 'super_admin'].includes(role) && tid;
      setCanEditTenantHome(Boolean(adminOk));
      setTenantId(tid || null);
      if (adminOk) {
        const tkn = localStorage.getItem('tkn');
        const res = await fetch(`/api/v1/tenants/${tid}`, {
          credentials: 'include',
          headers: {
            Accept: 'application/json',
            ...(tkn ? { Authorization: `Bearer ${tkn}` } : {}),
          },
        });
        if (res.ok) {
          const data = await res.json();
          setTenantHome(data.tenant?.homeDestination || { type: 'launchpad', id: null });
        }
      }
    } catch (error) {
      console.error('Error loading settings:', error);
    }
  };

  const saveSettings = async () => {
    try {
      setSaving(true);
      // Save to localStorage (in a real app, you'd save to backend)
      localStorage.setItem('userSettings', JSON.stringify(settings));

      // Apply theme change
      if (settings.theme !== 'dark') {
        const newDarkMode = settings.theme === 'dark';
        localStorage.setItem('theme', newDarkMode ? 'dark' : 'light');
        window.dispatchEvent(new CustomEvent('themeChanged', {
          detail: { darkMode: newDarkMode },
        }));
      }

      setSnackbar({ open: true, message: 'Settings saved successfully!', severity: 'success' });
    } catch (error) {
      console.error('Error saving settings:', error);
      setSnackbar({ open: true, message: 'Error saving settings', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleSettingChange = (key, value) => {
    setSettings((prev) => ({ ...prev, [key]: value }));
  };

  const addApiKey = () => {
    if (!newApiKey.name || !newApiKey.key) {
      setSnackbar({ open: true, message: 'Please fill in all required fields', severity: 'error' });
      return;
    }

    const apiKey = {
      id: Date.now().toString(),
      ...newApiKey,
      createdAt: new Date().toISOString(),
    };

    setSettings((prev) => ({
      ...prev,
      apiKeys: [...prev.apiKeys, apiKey],
    }));

    setNewApiKey({ name: '', key: '', description: '' });
    setApiKeyDialog(false);
    setSnackbar({ open: true, message: 'API key added successfully!', severity: 'success' });
  };

  const removeApiKey = (id) => {
    setSettings((prev) => ({
      ...prev,
      apiKeys: prev.apiKeys.filter((key) => key.id !== id),
    }));
    setSnackbar({ open: true, message: 'API key removed', severity: 'info' });
  };

  const addWebhook = () => {
    if (!newWebhook.name || !newWebhook.url) {
      setSnackbar({ open: true, message: 'Please fill in all required fields', severity: 'error' });
      return;
    }

    const webhook = {
      id: Date.now().toString(),
      ...newWebhook,
      createdAt: new Date().toISOString(),
      active: true,
    };

    setSettings((prev) => ({
      ...prev,
      webhooks: [...prev.webhooks, webhook],
    }));

    setNewWebhook({ name: '', url: '', events: [] });
    setWebhookDialog(false);
    setSnackbar({ open: true, message: 'Webhook added successfully!', severity: 'success' });
  };

  const removeWebhook = (id) => {
    setSettings((prev) => ({
      ...prev,
      webhooks: prev.webhooks.filter((webhook) => webhook.id !== id),
    }));
    setSnackbar({ open: true, message: 'Webhook removed', severity: 'info' });
  };

  const resetSettings = () => {
    localStorage.removeItem('userSettings');
    setSnackbar({ open: true, message: 'Settings reset to defaults', severity: 'info' });
    loadSettings();
  };

  const tabContent = [
    // Profile Tab
    <Grid container spacing={3}>
      {canEditTenantHome && tenantId && (
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <TenantHomeDestinationPanel
                tenantId={tenantId}
                initialDestination={tenantHome}
                onSaved={(homeDestination) => setTenantHome(homeDestination)}
              />
            </CardContent>
          </Card>
        </Grid>
      )}
      <Grid item xs={12}>
        <Card>
          <CardContent>
            <Typography
              variant="h6"
              sx={{
                mb: 2, display: 'flex', alignItems: 'center', gap: 1,
              }}
            >
              <PersonIcon />
              {' '}
              Profile Information
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Username"
                  value={settings.username}
                  onChange={(e) => handleSettingChange('username', e.target.value)}
                  disabled
                  helperText="Username cannot be changed"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Email"
                  type="email"
                  value={settings.email}
                  onChange={(e) => handleSettingChange('email', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="First Name"
                  value={settings.firstName}
                  onChange={(e) => handleSettingChange('firstName', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Last Name"
                  value={settings.lastName}
                  onChange={(e) => handleSettingChange('lastName', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Timezone</InputLabel>
                  <Select
                    value={settings.timezone}
                    onChange={(e) => handleSettingChange('timezone', e.target.value)}
                    label="Timezone"
                  >
                    <MenuItem value="UTC">UTC</MenuItem>
                    <MenuItem value="America/New_York">Eastern Time</MenuItem>
                    <MenuItem value="America/Chicago">Central Time</MenuItem>
                    <MenuItem value="America/Denver">Mountain Time</MenuItem>
                    <MenuItem value="America/Los_Angeles">Pacific Time</MenuItem>
                    <MenuItem value="Europe/London">London</MenuItem>
                    <MenuItem value="Europe/Paris">Paris</MenuItem>
                    <MenuItem value="Asia/Tokyo">Tokyo</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Language</InputLabel>
                  <Select
                    value={settings.language}
                    onChange={(e) => handleSettingChange('language', e.target.value)}
                    label="Language"
                  >
                    <MenuItem value="en">English</MenuItem>
                    <MenuItem value="es">Spanish</MenuItem>
                    <MenuItem value="fr">French</MenuItem>
                    <MenuItem value="de">German</MenuItem>
                    <MenuItem value="it">Italian</MenuItem>
                    <MenuItem value="pt">Portuguese</MenuItem>
                    <MenuItem value="ja">Japanese</MenuItem>
                    <MenuItem value="zh">Chinese</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Grid>
    </Grid>,

    // Appearance Tab
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Card>
          <CardContent>
            <Typography
              variant="h6"
              sx={{
                mb: 2, display: 'flex', alignItems: 'center', gap: 1,
              }}
            >
              <PaletteIcon />
              {' '}
              Appearance & Display
            </Typography>

            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Theme</InputLabel>
                  <Select
                    value={settings.theme}
                    onChange={(e) => handleSettingChange('theme', e.target.value)}
                    label="Theme"
                  >
                    <MenuItem value="light">Light</MenuItem>
                    <MenuItem value="dark">Dark</MenuItem>
                    <MenuItem value="auto">Auto (System)</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Font Size</InputLabel>
                  <Select
                    value={settings.fontSize}
                    onChange={(e) => handleSettingChange('fontSize', e.target.value)}
                    label="Font Size"
                  >
                    <MenuItem value="small">Small</MenuItem>
                    <MenuItem value="medium">Medium</MenuItem>
                    <MenuItem value="large">Large</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={(
                    <Switch
                      checked={settings.compactMode}
                      onChange={(e) => handleSettingChange('compactMode', e.target.checked)}
                    />
                  )}
                  label="Compact Mode"
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={(
                    <Switch
                      checked={settings.animationsEnabled}
                      onChange={(e) => handleSettingChange('animationsEnabled', e.target.checked)}
                    />
                  )}
                  label="Enable Animations"
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Grid>
    </Grid>,

    // Security Tab
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Card>
          <CardContent>
            <Typography
              variant="h6"
              sx={{
                mb: 2, display: 'flex', alignItems: 'center', gap: 1,
              }}
            >
              <SecurityIcon />
              {' '}
              Security & Privacy
            </Typography>

            <Grid container spacing={3}>
              <Grid item xs={12}>
                <FormControlLabel
                  control={(
                    <Switch
                      checked={settings.twoFactorEnabled}
                      onChange={(e) => handleSettingChange('twoFactorEnabled', e.target.checked)}
                    />
                  )}
                  label="Enable Two-Factor Authentication"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography gutterBottom>Session Timeout (minutes)</Typography>
                <Slider
                  value={settings.sessionTimeout}
                  onChange={(e, value) => handleSettingChange('sessionTimeout', value)}
                  min={5}
                  max={120}
                  marks={[
                    { value: 5, label: '5min' },
                    { value: 30, label: '30min' },
                    { value: 60, label: '1hr' },
                    { value: 120, label: '2hr' },
                  ]}
                  valueLabelDisplay="auto"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography gutterBottom>Data Retention (days)</Typography>
                <Slider
                  value={settings.dataRetention}
                  onChange={(e, value) => handleSettingChange('dataRetention', value)}
                  min={30}
                  max={365}
                  marks={[
                    { value: 30, label: '30d' },
                    { value: 90, label: '90d' },
                    { value: 180, label: '6m' },
                    { value: 365, label: '1y' },
                  ]}
                  valueLabelDisplay="auto"
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={(
                    <Switch
                      checked={settings.loginNotifications}
                      onChange={(e) => handleSettingChange('loginNotifications', e.target.checked)}
                    />
                  )}
                  label="Email notifications for new logins"
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Grid>
    </Grid>,

    // Notifications Tab
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Card>
          <CardContent>
            <Typography
              variant="h6"
              sx={{
                mb: 2, display: 'flex', alignItems: 'center', gap: 1,
              }}
            >
              <NotificationsIcon />
              {' '}
              Notification Preferences
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={12}>
                <FormControlLabel
                  control={(
                    <Switch
                      checked={settings.emailNotifications}
                      onChange={(e) => handleSettingChange('emailNotifications', e.target.checked)}
                    />
                  )}
                  label="Email Notifications"
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={(
                    <Switch
                      checked={settings.pushNotifications}
                      onChange={(e) => handleSettingChange('pushNotifications', e.target.checked)}
                    />
                  )}
                  label="Push Notifications"
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={(
                    <Switch
                      checked={settings.formSubmissionAlerts}
                      onChange={(e) => handleSettingChange('formSubmissionAlerts', e.target.checked)}
                    />
                  )}
                  label="Form Submission Alerts"
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={(
                    <Switch
                      checked={settings.weeklyReports}
                      onChange={(e) => handleSettingChange('weeklyReports', e.target.checked)}
                    />
                  )}
                  label="Weekly Activity Reports"
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Grid>
    </Grid>,

    // AI & Chat Tab
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Card>
          <CardContent>
            <Typography
              variant="h6"
              sx={{
                mb: 2, display: 'flex', alignItems: 'center', gap: 1,
              }}
            >
              <ApiIcon />
              {' '}
              AI & Chat Settings
            </Typography>

            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Default AI Model</InputLabel>
                  <Select
                    value={settings.defaultModel}
                    onChange={(e) => handleSettingChange('defaultModel', e.target.value)}
                    label="Default AI Model"
                  >
                    <MenuItem value="llama4">LLaMA 4 (Cloud)</MenuItem>
                    <MenuItem value="llama3">LLaMA 3 (Local)</MenuItem>
                    <MenuItem value="mixtral">Mixtral (Local)</MenuItem>
                    <MenuItem value="codellama">Code LLaMA (Local)</MenuItem>
                    <MenuItem value="gpt-oss">GPT-OSS (Local)</MenuItem>
                    <MenuItem value="openai">OpenAI (Cloud)</MenuItem>
                    <MenuItem value="deepseek">DeepSeek (Cloud)</MenuItem>
                    <MenuItem value="openaa">OpenAA (Cloud)</MenuItem>
                    <MenuItem value="gemini">Gemini (Cloud)</MenuItem>
                    <MenuItem value="anthropic">Anthropic (Cloud)</MenuItem>
                    <MenuItem value="azure">Azure (Cloud)</MenuItem>
                    <MenuItem value="mistral">Mistral (Cloud)</MenuItem>
                    <MenuItem value="cohere">Cohere (Cloud)</MenuItem>
                    <MenuItem value="together">Together (Cloud)</MenuItem>
                    <MenuItem value="custom">Custom</MenuItem>
                    <MenuItem value="ollama">Ollama (Local)</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Max Tokens"
                  type="number"
                  value={settings.maxTokens}
                  onChange={(e) => handleSettingChange('maxTokens', parseInt(e.target.value))}
                  inputProps={{ min: 100, max: 10000 }}
                />
              </Grid>
              <Grid item xs={12}>
                <Typography gutterBottom>
                  Temperature:
                  {settings.temperature}
                </Typography>
                <Slider
                  value={settings.temperature}
                  onChange={(e, value) => handleSettingChange('temperature', value)}
                  min={0}
                  max={2}
                  step={0.1}
                  marks={[
                    { value: 0, label: 'Deterministic' },
                    { value: 0.7, label: 'Balanced' },
                    { value: 2, label: 'Creative' },
                  ]}
                  valueLabelDisplay="auto"
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={(
                    <Switch
                      checked={settings.autoPlayAudio}
                      onChange={(e) => handleSettingChange('autoPlayAudio', e.target.checked)}
                    />
                  )}
                  label="Auto-play AI responses"
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={(
                    <Switch
                      checked={settings.speechRecognition}
                      onChange={(e) => handleSettingChange('speechRecognition', e.target.checked)}
                    />
                  )}
                  label="Enable speech recognition"
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Grid>
    </Grid>,

    // API & Integrations Tab
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Card>
          <CardContent>
            <Box sx={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2,
            }}
            >
              <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <ApiIcon />
                {' '}
                API Keys
              </Typography>
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={() => setApiKeyDialog(true)}
              >
                Add API Key
              </Button>
            </Box>

            {settings.apiKeys.length === 0 ? (
              <Typography color="text.secondary">No API keys configured</Typography>
            ) : (
              <List>
                {settings.apiKeys.map((apiKey) => (
                  <ListItem key={apiKey.id} divider>
                    <ListItemText
                      primary={apiKey.name}
                      secondary={`${apiKey.description} • Created: ${new Date(apiKey.createdAt).toLocaleDateString()}`}
                    />
                    <ListItemSecondaryAction>
                      <IconButton color="error" onClick={() => removeApiKey(apiKey.id)}>
                        <DeleteIcon />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>
            )}
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12}>
        <Card>
          <CardContent>
            <Box sx={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2,
            }}
            >
              <Typography variant="h6">Webhooks</Typography>
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={() => setWebhookDialog(true)}
              >
                Add Webhook
              </Button>
            </Box>

            {settings.webhooks.length === 0 ? (
              <Typography color="text.secondary">No webhooks configured</Typography>
            ) : (
              <List>
                {settings.webhooks.map((webhook) => (
                  <ListItem key={webhook.id} divider>
                    <ListItemText
                      primary={webhook.name}
                      secondary={(
                        <Box>
                          <Typography variant="body2">{webhook.url}</Typography>
                          <Box sx={{ mt: 0.5 }}>
                            {webhook.events.map((event) => (
                              <Chip key={event} label={event} size="small" sx={{ mr: 0.5 }} />
                            ))}
                          </Box>
                        </Box>
                      )}
                    />
                    <ListItemSecondaryAction>
                      <IconButton color="error" onClick={() => removeWebhook(webhook.id)}>
                        <DeleteIcon />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>
            )}
          </CardContent>
        </Card>
      </Grid>
    </Grid>,

    // LLM Usage Tab
    <UsageDashboard darkMode={false} />,
  ];

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3,
      }}
      >
        <Typography variant="h4" component="h1">
          Settings
        </Typography>
        <Stack direction="row" spacing={1} alignItems="center">
          <FullscreenButton />

          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={saveSettings}
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </Stack>
      </Box>

      {/* Tab Navigation */}
      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={selectedTab}
          onChange={(e, newValue) => setSelectedTab(newValue)}
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab label="Profile" />
          <Tab label="Appearance" />
          <Tab label="Security" />
          <Tab label="Notifications" />
          <Tab label="AI & Chat" />
          <Tab label="API & Integrations" />
          <Tab label="LLM Usage" />
        </Tabs>
      </Paper>

      {/* Tab Content */}
      <Paper sx={{ p: 3, mb: 3 }}>
        {tabContent[selectedTab]}
      </Paper>

      {/* Action Buttons */}
      <Paper sx={{ p: 2 }}>
        <Stack direction="row" spacing={2} justifyContent="space-between">
          <Button
            variant="outlined"
            color="warning"
            startIcon={<RestoreIcon />}
            onClick={resetSettings}
          >
            Reset to Defaults
          </Button>

          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={saveSettings}
          >
            Save Changes
          </Button>
        </Stack>
      </Paper>

      {/* API Key Dialog */}
      <Dialog open={apiKeyDialog} onClose={() => setApiKeyDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add API Key</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Name"
              value={newApiKey.name}
              onChange={(e) => setNewApiKey({ ...newApiKey, name: e.target.value })}
              fullWidth
              required
            />
            <TextField
              label="API Key"
              type="password"
              value={newApiKey.key}
              onChange={(e) => setNewApiKey({ ...newApiKey, key: e.target.value })}
              fullWidth
              required
            />
            <TextField
              label="Description"
              value={newApiKey.description}
              onChange={(e) => setNewApiKey({ ...newApiKey, description: e.target.value })}
              fullWidth
              multiline
              rows={2}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApiKeyDialog(false)}>Cancel</Button>
          <Button onClick={addApiKey} variant="contained">Add</Button>
        </DialogActions>
      </Dialog>

      {/* Webhook Dialog */}
      <Dialog open={webhookDialog} onClose={() => setWebhookDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Webhook</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Name"
              value={newWebhook.name}
              onChange={(e) => setNewWebhook({ ...newWebhook, name: e.target.value })}
              fullWidth
              required
            />
            <TextField
              label="Webhook URL"
              value={newWebhook.url}
              onChange={(e) => setNewWebhook({ ...newWebhook, url: e.target.value })}
              fullWidth
              required
              placeholder="https://your-domain.com/webhook"
            />
            <FormControl fullWidth>
              <InputLabel>Events</InputLabel>
              <Select
                multiple
                value={newWebhook.events}
                onChange={(e) => setNewWebhook({ ...newWebhook, events: e.target.value })}
                label="Events"
              >
                <MenuItem value="form.submitted">Form Submitted</MenuItem>
                <MenuItem value="flow.completed">Flow Completed</MenuItem>
                <MenuItem value="user.login">User Login</MenuItem>
                <MenuItem value="chat.message">Chat Message</MenuItem>
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setWebhookDialog(false)}>Cancel</Button>
          <Button onClick={addWebhook} variant="contained">Add</Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default Settings;
